import Dropdown, { DropdownChangeEventProps } from '@allesauto/core/dropdown';
import ResultsNumber from '@allesauto/core/resultsNumber';
import Sorting from '@allesauto/core/sorting';
import SwitchListTile from '@allesauto/core/switchListTile';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import translate from '@allesauto/i18n';
import { consts, ViewModes } from '@allesauto/utils/consts';
import { useIsWhiteLabel } from '@allesauto/utils/hooks.ts';
import { useIntl } from 'react-intl';

import { AccessoriesUrlSorting } from '@/features/Accessories/types';
import { WL_INTEGRATION_SLUG_WALDHAUSEN } from '@/features/whitelabel/util/getWhiteLabelIntegrationSlugs';
import { SortingOptions } from '@/store/redux/reducers/pages/pcp/page';

interface SortingItem {
  label: string;
  subject: string;
  direction: string;
}

export interface HorizontalToolbarProps {
  viewModeEnabled?: boolean;
  viewMode?: ViewModes;
  itemsPerPageEnabled?: boolean;
  itemsPerPage?: number;
  sorting?: SortingOptions | AccessoriesUrlSorting;
  resultsNumber: number;
  onViewTypeChange?: (value: ViewModes) => void;
  onItemsPerPageChange?: ({
    selectedIndex,
    value,
    text,
  }: DropdownChangeEventProps) => void;
  onSortingChange?: (value: SortingOptions) => void;
  disabled?: boolean;
  pcpType?: 'Vehicle' | 'Accessory';
  isAccessory?: boolean;
  whiteLabelDefaultSorting?: {
    direction: string;
    subject: string;
  };
}

export default function HorizontalToolbar({
  viewModeEnabled = true,
  viewMode = ViewModes.DEFAULT,
  sorting,
  resultsNumber,
  itemsPerPageEnabled = true,
  itemsPerPage,
  onViewTypeChange,
  onItemsPerPageChange,
  onSortingChange,
  disabled = false,
  pcpType = 'Vehicle',
  isAccessory = false,
  whiteLabelDefaultSorting,
}: HorizontalToolbarProps) {
  const intl = useIntl();
  const whiteLabel = useWhiteLabel();
  const isWaldhausen = useIsWhiteLabel(WL_INTEGRATION_SLUG_WALDHAUSEN);

  const {
    PCP: {
      HORIZONTAL_TOOLBAR: { RESULTS_PER_PAGE, RESULTS_PER_PAGE_DEFAULT },
    },
  } = consts;

  const sortingItemsKey =
    pcpType === 'Accessory' ? 'ACCESSORIES_SORTING_ITEMS' : 'SORTING_ITEMS';

  const sortingDefaultValKey =
    pcpType === 'Accessory'
      ? 'ACCESSORIES_SORTING_DEFAULT_VALUE'
      : 'SORTING_DEFAULT_VALUE';

  const numberOfSortTypes = {
    vehicles: 8,
    accessories: 6,
  };

  const numberOfItemsSorting = Array.from(
    Array(
      pcpType === 'Accessory'
        ? numberOfSortTypes.accessories
        : numberOfSortTypes.vehicles
    ).keys()
  );

  const sortingTypesItems = numberOfItemsSorting.map((item) => ({
    label: translate(
      intl,
      `ui.PCP.HORIZONTAL_TOOLBAR.${sortingItemsKey}.${item}.LABEL`
    ),
    subject: translate(
      intl,
      `ui.PCP.HORIZONTAL_TOOLBAR.${sortingItemsKey}.${item}.SUBJECT`
    ),
    direction: translate(
      intl,
      `ui.PCP.HORIZONTAL_TOOLBAR.${sortingItemsKey}.${item}.DIRECTION`
    ),
  }));

  const defaultValue = {
    label: translate(
      intl,
      `ui.PCP.HORIZONTAL_TOOLBAR.${sortingDefaultValKey}.LABEL`
    ),
    subject: translate(
      intl,
      `ui.PCP.HORIZONTAL_TOOLBAR.${sortingDefaultValKey}.SUBJECT`
    ),
    direction: translate(
      intl,
      `ui.PCP.HORIZONTAL_TOOLBAR.${sortingDefaultValKey}.DIRECTION`
    ),
  };

  const initialSortingValue =
    whiteLabelDefaultSorting && whiteLabel
      ? {
          label:
            sortingTypesItems.find(
              (item) =>
                item.subject === whiteLabelDefaultSorting.subject &&
                item.direction === whiteLabelDefaultSorting.direction
            )?.label ||
            translate(
              intl,
              `ui.PCP.HORIZONTAL_TOOLBAR.${sortingItemsKey}.DEFAULT_VALUE.LABEL`
            ),
          subject: whiteLabelDefaultSorting.subject,
          direction: whiteLabelDefaultSorting.direction,
        }
      : defaultValue;

  const value: SortingItem =
    sortingTypesItems.find(
      (item) =>
        item.subject === sorting?.subject &&
        item.direction === sorting?.direction
    ) || initialSortingValue;

  return (
    <div
      className={`!h-[60px] !justify-between flex items-center w-full wl-horizontal-toolbar-margin-top 
          md:!h-[90px] md:!border-b-1px md:!border-grey40 lg:after:absolute lg:after:!bg-grey40 lg:after:content-[''] 
          lg:after:h-[1px] lg:after:left-[25vw] lg:after:top-[90px] lg:after:w-[calc(100%-25vw)] lg:border-none`}
    >
      <ResultsNumber
        results={resultsNumber}
        text={translate(intl, 'ui.PCP.HORIZONTAL_TOOLBAR.RESULTS_NUMBER')}
        isAccessory={isAccessory}
      />

      <div className={`flex ${isWaldhausen && '!ml-auto'}`}>
        {viewModeEnabled && (
          <div className="hidden lg:block">
            <SwitchListTile
              disabled={disabled}
              label={translate(
                intl,
                'ui.PCP.HORIZONTAL_TOOLBAR.SWITCH_LIST_TILE'
              )}
              onChange={onViewTypeChange}
              viewMode={viewMode}
            />
          </div>
        )}

        {itemsPerPageEnabled && (
          <div className="hidden ml-30px lg:block xl:ml-40px">
            <Dropdown
              disabled={disabled}
              items={RESULTS_PER_PAGE}
              value={itemsPerPage || RESULTS_PER_PAGE_DEFAULT}
              label={translate(
                intl,
                'ui.PCP.HORIZONTAL_TOOLBAR.RESULTS_PER_PAGE'
              )}
              labelPosition="left"
              onChange={onItemsPerPageChange}
              hasGreyBg={!whiteLabel}
              context="horizontal-toolbar"
              whiteLabel={!!whiteLabel}
            />
          </div>
        )}

        <div className="lg:ml-30px xl:ml-40px">
          <Sorting
            disabled={disabled}
            items={sortingTypesItems}
            defaultValue={value}
            onChange={onSortingChange}
            hasGreyBg={!whiteLabel}
            context="horizontal-toolbar"
            whiteLabelIntegrationSlug={useWhiteLabel()?.subdomain || undefined}
          />
        </div>
      </div>
    </div>
  );
}
